"use client";

import { useCallback } from "react";
import { useSearchParams } from "next/navigation";
import useRouterParams from "./useRouterParams";

interface Options<T extends string = string> {
  validValues?: Set<T>;
}

/**
 * Single-value URL param backed by `useRouterParams.setParams`. Returns the
 * raw value when no validation set is supplied, or the value only when it
 * passes membership in `validValues`. Always resets `page` to 1 on write so
 * pagination doesn't carry across filter changes.
 */
export default function useSingleValueParameter<T extends string = string>(
  parameterName: string,
  options: Options<T> = {},
) {
  const searchParameters = useSearchParams();
  const { setParams } = useRouterParams();

  const raw = searchParameters?.get(parameterName) ?? undefined;
  const value =
    raw && (!options.validValues || options.validValues.has(raw as T))
      ? (raw as T)
      : undefined;

  const set = useCallback(
    (next: T) => {
      setParams({ [parameterName]: next, page: 1 });
    },
    [parameterName, setParams],
  );

  const clear = useCallback(() => {
    setParams({ page: 1 }, [parameterName]);
  }, [parameterName, setParams]);

  return { value, set, clear };
}
